import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const STATUSES = ['draft', 'ready_for_review', 'approved', 'rejected'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOdHandoversApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-handovers?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Load a single handover plan (with itemsJson) so HR can view/fill it on behalf.
  const getForOffboarding = useCallback(async (offboardingId) => {
    const res = await api.get(`/v1/offboarding-handovers/for-offboarding/${offboardingId}`);
    return res?.data?.data || null;
  }, []);

  // HR fills/edits the handover items on behalf of the leaver (OD.Write).
  const saveItems = useCallback(async (offboardingId, itemsJson) => {
    try {
      await api.put(`/v1/offboarding-handovers/for-offboarding/${offboardingId}`, { offboardingId, itemsJson });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const submit = useCallback(async (offboardingId) => {
    try { await api.post(`/v1/offboarding-handovers/for-offboarding/${offboardingId}/submit`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const approve = useCallback(async (offboardingId, note) => {
    try { await api.post(`/v1/offboarding-handovers/for-offboarding/${offboardingId}/approve`, { note }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const reject = useCallback(async (offboardingId, note) => {
    try { await api.post(`/v1/offboarding-handovers/for-offboarding/${offboardingId}/reject`, { note }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`offboardingHandover.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'offboardingId', label: 'OD', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboardingHandover.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'status',
        label: t('offboardingHandover.statusLabel'),
        options: {
          ...multiSelectFilter(t('offboardingHandover.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      numericFilterColumn('itemCount', t('offboardingHandover.itemCount'), { decimals: 0 }),
      {
        name: 'managerSignedOffAt',
        label: t('offboardingHandover.signedOff'),
        options: {
          filter: false, sort: true,
          customBodyRender: (v) => v ? new Date(v).toLocaleString() : '–',
        },
      },
      {
        name: 'employeeAcknowledgedAt',
        label: t('offboardingHandover.acknowledged'),
        options: {
          filter: false, sort: true,
          customBodyRender: (v) => v ? new Date(v).toLocaleString() : '–',
        },
      },
    ];
  }, [t, data]);

  return { fetching, data, pagination, columns, refresh, getForOffboarding, saveItems, submit, approve, reject };
};

export default useOdHandoversApi;
